import * as React from "react";
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLanguage } from "./Language";
import { Marc21Record, Marc21Subfield } from "./MARCRecord";
import axios from "axios";
import { useIsMobile } from "@tlc/ui/useIsMobile";

interface MarcFieldDataProps {
  subfields: Marc21Subfield[];
}

export const MarcFieldData: React.FC<MarcFieldDataProps> = ({ subfields }) => {
  const fieldPrefix = "\u2021";

  return (
    <>
      {subfields.map((sf, index) => (
        <React.Fragment key={`${sf.name}-${index}`}>
          {index > 0 && <wbr />}
          <Typography
            display="inline"
            variant="body1"
            sx={{
              color: "secondary.main",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {`${fieldPrefix + sf.name}`}
          </Typography>
          <Typography
            display="inline"
            variant="body1"
            // We can bring this back if its desired, leaving this in Just in case
            // sx={{
            //   "&:hover": {
            //     border: "1px solid",
            //     borderColor: "#0000ff",
            //   },
            // }}
          >
            {sf.data}
          </Typography>
          <wbr />
        </React.Fragment>
      ))}
    </>
  );
};

interface MarcRecordTableProps {
  resourceId: number;
  initialRecord?: Marc21Record; //storybook
}

export const MarcRecordTable: React.FC<MarcRecordTableProps> = ({
  resourceId,
  initialRecord,
}) => {
  const { translate } = useLanguage();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const [record, setRecord] = React.useState<Marc21Record | null>(
    initialRecord || null,
  );
  const [recordError, setRecordError] = React.useState(false);

  React.useEffect(() => {
    if (initialRecord) return; //storybook
    getData();
  }, [resourceId]);

  const getData = async () => {
    try {
      setRecordError(false);
      const { data } = await axios.get<Marc21Record>(
        `/marcrecord/${resourceId}`,
      );

      setRecord(data);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setRecordError(true);
        console.error("API error:", error.response?.status, error.message);
      } else {
        console.error("Unknown error:", error);
      }
    }
  };

  const header = [
    { id: "marcViewDescHeading", align: "left" as const },
    { id: "marcViewTagHeading", align: "center" as const },
    { id: "marcViewIndic1Heading", align: "center" as const },
    { id: "marcViewIndic2Heading", align: "center" as const },
    { id: "marcViewFieldDataHeading", align: "center" as const },
  ];

  const cells = {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: { xs: "0.75rem", sm: "0.875rem" },
  };

  const row = {
    "& td, & th": {
      ...cells,
      verticalAlign: "top",
    },
  };

  const noWrapCell = {
    whiteSpace: "nowrap",
  };

  const fieldDataCell = {
    whiteSpace: "pre-wrap", //preserve whitespace, but wrap when necessary
    wordBreak: "normal",
    overflowWrap: "break-word",
  };

  const numberPad = (n: number): string => {
    return String(n).padStart(3, "0");
  };

  const hideFieldDescription = useMediaQuery("(max-width: 500px)");
  const headerIsSticky = window.innerHeight >= 400; //We can adjust this threshold if it still is not satisfactory

  return (
    <TableContainer>
      {!recordError && (
        <>
          <Table
            stickyHeader={headerIsSticky}
            size={"small"}
            sx={{
              border: "1px solid",
              borderColor: "#333",
              // 300px sweet spot through trial and error (accounts for vertical positioning, DialogTitle, DialogActions and padding/margins)
              // This is copied from the original code to maintain congruency with previous functionality
              height: isMobile ? "calc(100vh - 150px)" : "calc(100vh - 300px)",
              overflowX: { xs: "scroll" },
              boxSizing: "border-box",
            }}
            aria-label="Marc Record Table"
          >
            <TableHead>
              <TableRow
                sx={{
                  whiteSpace: "nowrap",
                  "& td, & th": {
                    border: "1px solid",
                    borderColor: "#333",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                {header.map(({ id, align }) => {
                  if (id === "marcViewDescHeading" && hideFieldDescription)
                    return null;
                  return (
                    <TableCell
                      key={id}
                      id={id}
                      align={align}
                      sx={{
                        ...cells,
                        px: isMobile ? 0 : 0.75,
                        py: 0.25,
                      }}
                    >
                      <Typography fontWeight="bold">{translate(id)}</Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {record?.leader && (
                <TableRow hover sx={row}>
                  {!hideFieldDescription && (
                    <TableCell align="left" sx={{ noWrapCell }}>
                      <span>Leader</span>
                    </TableCell>
                  )}
                  <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={"Leader"}>
                      <span tabIndex={0}>LDR</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">&nbsp;</TableCell>
                  <TableCell align="center">&nbsp;</TableCell>
                  <TableCell align="left">
                    <Typography display="inline" variant="body1">
                      {record.leader}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {record?.fields.map((field, index) => (
                <TableRow hover key={`${field.tag}-${index}`} sx={row}>
                  {!hideFieldDescription && (
                    <TableCell align="left" sx={{ ...noWrapCell }}>
                      <span>{field.label}</span>
                    </TableCell>
                  )}
                  <TableCell align="left">
                    <Tooltip title={field.label}>
                      <span tabIndex={0}>{numberPad(field.tag)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ px: 0 }}>
                    {field.indicator1 || "&nbsp;"}
                  </TableCell>
                  <TableCell align="center" sx={{ px: 0 }}>
                    {field.indicator2 || "&nbsp;"}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      ...fieldDataCell,
                    }}
                  >
                    {field.tag < 10 ? (
                      <Typography
                        display="inline"
                        variant="body1"
                        sx={{
                          whiteSpace: "nowrap",
                          fontFamily: "'Roboto Mono', monospace",
                        }}
                      >
                        {field.data}
                      </Typography>
                    ) : (
                      <MarcFieldData subfields={field.subfields} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {recordError && (
        <Alert severity="error">
          <Typography>{translate("marcRecordErrorMessage")}</Typography>
        </Alert>
      )}
    </TableContainer>
  );
};
